import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, UserPlus, Trash2, Search, Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function SubBrokerClientMapping() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const [subbrokers, setSubbrokers] = useState([]);
  const [selectedSb, setSelectedSb] = useState(params.get("sb") || "");
  const [mappings, setMappings] = useState([]);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const refreshSubBrokers = async () => {
    try {
      const { data } = await api.get("/subbroker/list");
      setSubbrokers(data.subbrokers || []);
    } catch (e) {
      console.warn("[SubBrokerClientMapping] refreshSubBrokers failed", e);
    }
  };

  const refreshMappings = async () => {
    if (!selectedSb) return;
    try {
      const m = await api.get(`/subbroker/${selectedSb}/clients`);
      setMappings(m.data.mappings || []);
    } catch (e) {
      console.warn("[SubBrokerClientMapping] refreshMappings failed", e);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get("/subbroker/client-mapping/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "client_mapping_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/subbroker/client-mapping/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      const errs = (data.errors || []).length;
      toast.success(`${data.inserted} mapped · ${data.reactivated || 0} reactivated · ${data.auto_created_associates || 0} new associates · ${errs} error${errs === 1 ? "" : "s"}`);
      // Refresh both panes after upload
      await refreshSubBrokers();
      await refreshMappings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subbroker/list");
        setSubbrokers(data.subbrokers || []);
        if (!selectedSb && data.subbrokers?.[0]) setSelectedSb(data.subbrokers[0].id);
      } catch (e) { toast.error("Failed to load sub brokers"); }
    })();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (!selectedSb) return;
    setLoading(true);
    (async () => {
      try {
        const [m, a] = await Promise.all([
          api.get(`/subbroker/${selectedSb}/clients`),
          api.get(`/subbroker/client-mapping/available-clients?limit=300&search=${encodeURIComponent(search)}`),
        ]);
        setMappings(m.data.mappings || []);
        setAvailable(a.data.clients || []);
      } finally { setLoading(false); }
    })();
  }, [selectedSb, search, api]);

  const togglePick = (key) => {
    setPicked((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleMap = async () => {
    if (picked.size === 0) { toast.error("Select at least one client"); return; }
    const pans = [], name_keys = [];
    for (const k of picked) {
      const client = available.find((c) => c.key === k);
      if (!client) continue;
      if (client.pan) pans.push(client.pan); else name_keys.push(client.name);
    }
    try {
      const { data } = await api.post(`/subbroker/${selectedSb}/client-mapping`, { pans, name_keys });
      toast.success(`${data.inserted} mapped · ${data.duplicates_skipped} duplicates skipped`);
      setPicked(new Set());
      const m = await api.get(`/subbroker/${selectedSb}/clients`);
      setMappings(m.data.mappings || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Mapping failed"); }
  };

  const handleUnmap = async (mappingId) => {
    if (!window.confirm("Unmap this client?")) return;
    await api.delete(`/subbroker/${selectedSb}/client-mapping/${mappingId}`);
    const m = await api.get(`/subbroker/${selectedSb}/clients`);
    setMappings(m.data.mappings || []);
  };

  return (
    <div className="space-y-6" data-testid="subbroker-mapping-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Client Mapping</h1>
        <p className="text-sm text-gray-500">Assign clients (by PAN or Name when PAN is missing) to each sub broker</p>
      </div>

      {/* Bulk upload card */}
      <Card className="p-5 space-y-4 border-teal-100 bg-teal-50/30" data-testid="bulk-upload-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-teal-700" />
              Bulk Client Mapping Upload
            </h3>
            <p className="text-xs text-gray-600 mt-1 max-w-2xl">
              Upload an XLSX or CSV with columns <span className="font-mono text-[11px] bg-white px-1.5 rounded border">Sub Broker Code</span>,
              <span className="font-mono text-[11px] bg-white px-1.5 rounded border ml-1">Client PAN</span>,
              <span className="font-mono text-[11px] bg-white px-1.5 rounded border ml-1">Client Name</span>,
              <span className="font-mono text-[11px] bg-white px-1.5 rounded border ml-1">Guardian PAN</span>.
              PAN is preferred; Name is used as fallback when PAN is missing. Unknown Associate Codes are auto-created as pending stubs.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadTemplate}
              data-testid="download-template-btn"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Template
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileSelected}
              data-testid="bulk-upload-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{ backgroundColor: "#1a3f3a" }}
              className="gap-2 text-white"
              data-testid="bulk-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Upload File"}
            </Button>
          </div>
        </div>

        {uploadResult && (
          <div
            className="rounded-md border bg-white p-3 text-xs space-y-2"
            data-testid="bulk-upload-result"
          >
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-700">
              <span><span className="font-semibold text-emerald-700">{uploadResult.inserted}</span> newly mapped</span>
              <span><span className="font-semibold text-blue-700">{uploadResult.reactivated || 0}</span> reactivated</span>
              <span><span className="font-semibold text-purple-700">{uploadResult.auto_created_associates || 0}</span> associates auto-created</span>
              <span><span className="font-semibold text-gray-700">{uploadResult.total}</span> rows processed</span>
              <span><span className="font-semibold text-rose-700">{(uploadResult.errors || []).length}</span> errors</span>
            </div>
            {(uploadResult.errors || []).length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-rose-700 font-medium">
                  Show {uploadResult.errors.length} error{uploadResult.errors.length === 1 ? "" : "s"}
                </summary>
                <div className="max-h-48 overflow-y-auto mt-2 border-t pt-2 space-y-1">
                  {uploadResult.errors.slice(0, 50).map((e, i) => (
                    <div key={`${e.row ?? "row"}-${i}`} className="text-[11px] text-gray-600">
                      <span className="font-mono">Row {e.row}</span>: {e.error}
                    </div>
                  ))}
                  {uploadResult.errors.length > 50 && (
                    <div className="text-[11px] text-gray-500 italic">…and {uploadResult.errors.length - 50} more</div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <Label className="text-xs">Pick Sub Broker</Label>
        <select value={selectedSb} onChange={(e) => setSelectedSb(e.target.value)} className="border rounded-md h-10 px-3 text-sm w-full max-w-md" data-testid="sbm-select-subbroker">
          <option value="">— select —</option>
          {subbrokers.map((s) => (
            <option key={s.id} value={s.id}>{s.sub_broker_code} · {s.sub_broker_name} ({s.mapped_clients || 0} mapped)</option>
          ))}
        </select>
      </Card>

      {selectedSb && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-2 text-gray-800">Available Clients</h3>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input placeholder="Search by PAN or name…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sbm-search" />
            </div>
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-6 text-gray-500" /> : (
              <div className="max-h-[420px] overflow-y-auto border rounded-md divide-y">
                {available.length === 0 ? <p className="text-center text-gray-400 p-6 text-sm">No clients found</p> :
                  available.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer text-sm" data-testid={`sbm-avail-${c.pan || c.name}`}>
                      <input type="checkbox" checked={picked.has(c.key)} onChange={() => togglePick(c.key)} />
                      <span className="font-mono text-xs text-gray-500 w-24">{c.pan || "—"}</span>
                      <span className="flex-1">{c.name || "(no name)"}</span>
                      {!c.has_pan && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">NAME-FALLBACK</span>}
                    </label>
                  ))}
              </div>
            )}
            <Button onClick={handleMap} disabled={picked.size === 0} className="w-full mt-3" style={{ backgroundColor: "#1a3f3a" }} data-testid="sbm-map-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Map {picked.size} client{picked.size === 1 ? "" : "s"}
            </Button>
          </Card>

          <Card className="p-4">
            <h3 className="font-semibold mb-2 text-gray-800">Mapped Clients ({mappings.length})</h3>
            <div className="max-h-[500px] overflow-y-auto border rounded-md divide-y">
              {mappings.length === 0 ? <p className="text-center text-gray-400 p-6 text-sm">No clients mapped yet</p> :
                mappings.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 text-sm" data-testid={`sbm-mapped-${m.pan || m.name_key}`}>
                    <span className="font-mono text-xs text-gray-500 w-24">{m.pan || "—"}</span>
                    <span className="flex-1">{m.name_key || "(by PAN)"}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleUnmap(m.id)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
